import {
  CommonModule,
  LOCATION_INITIALIZED,
  registerLocaleData,
} from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injector,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarConfig,
  MatSnackBarModule,
} from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/browser';
import { AppRoutingModule } from './app-routing.module';
import { AuthModule } from './auth/auth.module';
import { AuthInterceptor } from './auth/auth.interceptor';
import { SidebarModule } from './sidebar/sidebar.module';
import { ReactiveFormsModule } from '@angular/forms';
import { SentryErrorHandler } from './sentry.error-handler';
import { EditModule } from './cms/edit/edit.module';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { SharedModule } from './shared/shared.module';
import { environment } from '../environments/environment';
import { ApiModule } from '../app/api/api.module';
import { ApiKeyInterceptor } from './api-key.interceptor';
import { ErrorInterceptor } from './error.interceptor';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function appInitializerFactory(
  translate: TranslateService,
  injector: Injector
) {
  return () =>
    new Promise<any>((resolve: any) => {
      const locationInitialized = injector.get(
        LOCATION_INITIALIZED,
        Promise.resolve(null)
      );
      locationInitialized.then(() => {
        let langToSet: any = translate.getBrowserLang();
        translate.setDefaultLang('en');
        try {
          if (localStorage.getItem('current-language')) {
            langToSet = localStorage.getItem('current-language');
          }
        } catch (err) {}
        if (!(langToSet === 'de' || langToSet === 'en')) {
          langToSet = 'en';
        }
        translate.use(langToSet).subscribe(
          () => {
            console.info(`Successfully initialized '${langToSet}' language.'`);
          },
          () => {
            console.info(
              `Problem with '${langToSet}' language initialization.`
            );
            resolve(null);
          },
          () => {
            resolve(null);
          }
        );
      });
    });
}

if (environment.sentry.dsn && environment.production) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    defaultIntegrations: false,
    enableLogs: true,
    integrations: Sentry.getDefaultIntegrations({}),
  });
}

registerLocaleData(localeDe, 'de');

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      ApiModule.forRoot({ rootUrl: environment.api.url }),
      AuthModule,
      BrowserModule,
      CommonModule,
      EditModule,
      SharedModule,
      EffectsModule.forRoot([]),
      MatBadgeModule,
      MatButtonModule,
      MatDividerModule,
      MatFormFieldModule,
      MatIconModule,
      MatInputModule,
      MatListModule,
      MatSnackBarModule,
      ReactiveFormsModule,
      SidebarModule,
      StoreModule.forRoot({}),
      StoreDevtoolsModule.instrument({
        logOnly: environment.production,
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      AppRoutingModule
    ),
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiKeyInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: 'de',
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'fill' },
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { duration: 5000 } as MatSnackBarConfig,
    },
    provideAppInitializer(() => {
      const initializerFn = appInitializerFactory(
        inject(TranslateService),
        inject(Injector)
      );
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
};
